import type { FlexProps, SpanProps } from '@chakra-ui/react';
import { Flex, Span } from '@chakra-ui/react';
import type { ReactNode } from 'react';
import { memo } from 'react';
import type { Session } from '@halo/chat-sdk';
import {
  isMyMessage,
  type Msg,
  isSessionCategory,
  SessionCategoryPrefix,
  StateEnum,
  MsgPayloadType,
  TransferStatus,
} from '@halo/chat-sdk';
import type { ReadUsersState } from '@halo/ui-chat-react';
import {
  MessagingStatusReadOverlay,
  TimeIcon,
  MessageStatusSingle,
  MessageStatusGroup,
} from '@halo/ui-chat-react';
import { useAppStore } from '@halo/chat-sdk-react';
import { useItemExpireContext } from '../../ItemWithExpireAnimate/context';

export interface MessageExpireProps extends SpanProps {}

export const MessageExpire = memo(function MessageExpire(props: MessageExpireProps) {
  const { displayTime, alwaysShowTime } = useItemExpireContext();
  const opacity = useMemo(() => {
    return alwaysShowTime ? 1 : 0;
  }, [alwaysShowTime]);

  if (!displayTime) {
    return (
      <Span fontSize="xs" mr="-0.5em">
        &nbsp;
      </Span>
    );
  }

  return (
    <Span
      className="MessageHeader_expire"
      fontSize="xs"
      fontWeight="medium"
      color="fg.subtle"
      whiteSpace="nowrap"
      display="inline-flex"
      alignItems="center"
      gap="1"
      opacity={opacity}
      {...props}
    >
      {displayTime}
      <TimeIcon />
    </Span>
  );
});

export interface MessageReadStatusProps {
  message: Msg;
  session: Session;
  hideMember?: boolean;
}

export const MessageReadStatus = memo(function MessageReadStatus({
  message,
  session,
  hideMember,
}: MessageReadStatusProps) {
  const { user, client } = useAppStore();
  const isMy = useMemo(() => {
    return isMyMessage(message, user?.user_id);
  }, [message, user?.user_id]);

  const isGroup = useMemo(
    () => isSessionCategory(session?.chat_id, SessionCategoryPrefix.Group),
    [session?.chat_id]
  );

  const onClickError = useCallback(() => {
    if (
      message.payload_type === MsgPayloadType.TRANSFER &&
      message.payload.status !== TransferStatus.Success
    ) {
      return;
    }
    client?.Api.messages.sendLocalMessage(session!.chat_id, message.cli_msg_id);
  }, [
    client?.Api.messages,
    message.cli_msg_id,
    message.payload?.status,
    message.payload_type,
    session,
  ]);

  const requestUsers = useCallback(
    async (cb: (data: ReadUsersState) => void) => {
      return client?.Api.messages.getExpectReadAndReaders(
        session?.chat_id ?? '',
        message.message_id,
        (readUsers, unreadUsers, users) => {
          users.forEach((user) => {
            // @ts-expect-error ignore
            user.error = !(user.have_group_key && user.have_user_key);
            // @ts-expect-error ignore
            user.mention = message.at_list.includes(user.user_id);
          });

          cb({
            readUsers,
            unreadUsers: unreadUsers.filter((m) => m.state !== StateEnum.DELETE_ACCOUNT),
          });
        }
      );
    },
    [client, message.at_list, message.message_id, session?.chat_id]
  );

  if (!isMy) {
    return null;
  }

  if (isGroup) {
    return (
      <MessageStatusGroup
        status={message.status}
        readCount={message.read_state?.read_count}
        totalCount={message.read_state?.total_count}
        onClickError={onClickError}
        hideMember={hideMember}
        renderProcess={(node) => (
          <MessagingStatusReadOverlay requestUsers={requestUsers} hideMember={hideMember}>
            {node}
          </MessagingStatusReadOverlay>
        )}
        showErrorText={true}
      />
    );
  }

  return (
    <MessageStatusSingle
      status={message.status}
      receiptStatus={message.receipt_status}
      onClickError={onClickError}
      showErrorText={true}
    />
  );
});

export interface MessageMetaRootProps extends FlexProps {
  children?: ReactNode;
}
export const MessageMetaRoot = memo(function MessageMetaRoot(props: MessageMetaRootProps) {
  return (
    <Flex
      mt="1"
      className="MessageMeta"
      gap="1.5"
      color="fg.muted"
      fontWeight="medium"
      fontSize="xs"
      alignItems="center"
      // justifyContent="flex-end"
      flexWrap="wrap"
      {...props}
    />
  );
});
